// store/modules/InventoryEmplacementModule.ts
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import InventoryEmplacementService from '@/Services/InventoryEmplacementService';
import { Inventaire } from '@/models/Inventaire';
import { InventaireEmplacement, InventaireEmplacementCreation } from '@/models/InventaireEmplacement';
import { isErrorResponse } from '@/utils/typeGuards';

@Module({ namespaced: true })
export default class InventoryEmplacementModule extends VuexModule {
    inventaires: Inventaire[] = [];
    error: string | null = null;
    inventaire: Inventaire | null = null;

    @Mutation
    setInventaires(inventaires: Inventaire[]) {
        this.inventaires = inventaires;
    }

    @Mutation
    setInventaire(inventaire: Inventaire) {
        this.inventaire = inventaire;
    }

    @Mutation
    setError(error: string | null) {
        this.error = error;
    }

    @Action
    async fetchInventaires(url: string) {
        try {
            console.log('🔍 INVENTORY EMPLACEMENT MODULE - URL reçue:', url);

            const response = await InventoryEmplacementService.getAll(url);

            // Si c'est un export (blob), retourner directement la réponse AxiosResponse
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            if (isExport) {
                return response; // AxiosResponse avec blob
            }

            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while fetching inventories.');
                return null;
            }

            // Stocker les données dans Vuex (seulement pour les requêtes normales)
            if (response.data) {
                this.context.commit('setInventaires', response.data);
            }

            console.log('🔍 INVENTORY EMPLACEMENT MODULE - Données reçues:', {
                dataLength: response.data?.length,
                totalRows: response.totalRows,
                recordsFiltered: response.recordsFiltered,
                recordsTotal: response.recordsTotal
            });

            // Retourner la réponse complète pour supporter DataTables
            return response;
        } catch (error: any) {
            console.error('🔍 INVENTORY EMPLACEMENT MODULE - Erreur:', error);
            this.context.commit('setError', error.message || 'An error occurred while fetching inventories.');
            return null;
        }
    }

    @Action
    async extractInventaires(queryParams: string) {
        try {
            const response = await InventoryEmplacementService.extractAll(queryParams);
            return response; // Retourner directement la réponse blob
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while extracting inventaires.');
            throw error;
        }
    }

    @Action
    async fetchInventairesArchive(url: string) {
        try {
            const response = await InventoryEmplacementService.getInventaireArchive(url);
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while extracting inventaires.');
                return null;
            }
            // Retourner le format DataTable complet
            return {
                data: response.data,
                totalRows: response.totalRows,
                recordsFiltered: response.recordsFiltered,
                recordsTotal: response.recordsTotal,
                draw: response.draw
            };
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while fetching inventaires archive.');
            return null;
        }
    }
    @Action
    async archiveInventaire({ id, commentaire, action }: { id: number, commentaire?: string, action: string }) {
        try {
            const response = await InventoryEmplacementService.archiveInventaire({ id, commentaire: commentaire || '', action });

            // Vérifiez si une réponse valide est retournée
            if (response && response.message) {
                // Met à jour l'état Vuex si nécessaire
                this.context.commit('setInventaires', response);
                return response; // Retourne { message: "Inventaire archived successfully" }
            } else {
                throw new Error('Réponse invalide du service.');
            }
        } catch (error: any) {
            // Enregistre et retourne une erreur cohérente
            const errorMessage = error.message || 'Une erreur est survenue lors de l\'archivage.';
            this.context.commit('setError', errorMessage);
            throw new Error(errorMessage);
        }
    }

    @Action
    async AnnulerAffectation(id: number) {
        try {
            const response = await InventoryEmplacementService.AnnulerAffectation({ id });

            if (response && response.message) {

                return response;
            } else {
                console.error("Réponse invalide du service.");
            }
        } catch (error: any) {
            console.error("Erreur lors de l'annulation de l'affectation :", error);
        }
    }

    @Action
    async createInventaire(inventaire: InventaireEmplacementCreation) {
        try {
            const response = await InventoryEmplacementService.create(inventaire);
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while creating inventory.');
                return null;
            }
            return response;
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while creating inventory.');
            throw error;
        }
    }

    @Action
    async getInventaireById(id: number) {
        try {
            const response = await InventoryEmplacementService.getByIdInventaire(id);
            return response;
        } catch (error: any) {
            return error;
        }
    }

    @Action
    async updateInventaire({ Id, Inventaire }: { Id: number, Inventaire: any }) {
        try {
            console.log("inventaire: " + Inventaire);

            const response = await InventoryEmplacementService.updateInventaire({ Id, Inventaire });
            return response;
        } catch (error: any) {
            return error;
        }
    }
    @Action
    async extractInventairesArchives({ page, pageSize, sortBy, sortOrder, search }: { page: number; pageSize: number; sortBy: string; sortOrder: string; search: string }) {
        try {
            const { data, totalRows } = await InventoryEmplacementService.extractAllArchives(page, pageSize, sortBy, sortOrder, search);
            return { data, totalRows };
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while fetching inventaires.');
            throw error;
        }
    }

    get getAll() {
        return this.inventaires;
    }
    get getInventaire() {
        return this.inventaire;
    } 
}
